'use client';

import { c as _c } from "react/compiler-runtime";
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { Collapse } from '@/uiComposites/interactive/Collapse';
import { Fragment, useId, useState } from 'react';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { box } from '@/uiPrimitives/layout/box';
import { stack } from '@/uiPrimitives/layout/stack';
import { locationOpeningTimesGrid } from './ContactBlockLocation.css';
import { warningIcon } from '@/uiIcons/warningIcon.css';
import { delayIconTransitionHover } from '@/uiComposites/interactive/accordion/Accordion.css';
import { locationCircleIcon } from '@/uiIcons/locationCircleIcon.css';
import { languageCode } from '@/globals/config/localization.config';
import { translateInline } from '@/globals/translate/translateInline';
export type Location = {
  title: string;
  address: string;
  zipcode: string;
  city: string;
  openingHoursMessageNL?: string; // only available in Dutch :(
  openingHours: Record<string, Array<string>>;
} | undefined;
const dayLabels = {
  monday: translateInline({
    en: 'Monday',
    nl: 'Maandag'
  }),
  tuesday: translateInline({
    en: 'Tuesday',
    nl: 'Dinsdag'
  }),
  wednesday: translateInline({
    en: 'Wednesday',
    nl: 'Woensdag'
  }),
  thursday: translateInline({
    en: 'Thursday',
    nl: 'Donderdag'
  }),
  friday: translateInline({
    en: 'Friday',
    nl: 'Vrijdag'
  }),
  saturday: translateInline({
    en: 'Saturday',
    nl: 'Zaterdag'
  }),
  sunday: translateInline({
    en: 'Sunday',
    nl: 'Zondag'
  })
};
export const ContactBlockLocation = t0 => {
  const $ = _c(67);
  const {
    location
  } = t0;
  const [isOpened, setIsOpened] = useState(false);
  const idSuffix = useId();
  if (location == null) {
    return null;
  }
  const {
    title,
    address,
    zipcode,
    city,
    openingHours,
    openingHoursMessageNL
  } = location;
  const id = `location-${title}-${idSuffix}`;
  let t1;
  if ($[0] !== isOpened) {
    t1 = () => setIsOpened(!isOpened);
    $[0] = isOpened;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classes(shelf({
      gap: "2 | h2",
      alignItems: "start"
    }));
    t3 = occupy({
      blockSize: "-1 | small"
    });
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <i aria-hidden={true} className={classes(icon({
      icon: locationCircleIcon,
      display: "inline-block",
      blockSize: "auto",
      inlineSize: "4 | betweenListItems",
      fill: "positiveBlue"
    }))} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isOpened) {
    t6 = classes(shelf({
      gap: "-1 | small",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      alignItems: "baseline"
    }), textStyle({
      color: "positiveBlue",
      fontWeight: isOpened ? "bold" : undefined
    }));
    $[8] = isOpened;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classes(textBlock({}));
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7 || $[12] !== title) {
    t8 = <h3 className={t7}>{title}</h3>;
    $[11] = t7;
    $[12] = title;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isOpened) {
    t9 = classes(delayIconTransitionHover, icon({
      icon: chevronIcon,
      adjustIcon: "rotate90deg",
      flip: isOpened ? "horizontal:hover" : "horizontalNone:hover",
      blockSize: "1ex"
    }));
    $[14] = isOpened;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <i className={t9} />;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6 || $[20] !== t8) {
    t11 = <span className={t6}>{t8}{t10}</span>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== id || $[23] !== isOpened || $[24] !== t1 || $[25] !== t11 || $[26] !== t2 || $[27] !== t5) {
    t12 = <button onClick={t1} aria-controls={id} aria-expanded={isOpened} className={t2}>{t5}{t11}</button>;
    $[22] = id;
    $[23] = isOpened;
    $[24] = t1;
    $[25] = t11;
    $[26] = t2;
    $[27] = t5;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  let t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classes(box({
      paddingBlockStart: "3 | h1",
      paddingBlockEnd: "0 | p",
      paddingInlineStart: "7 | betweenH3Sections"
    }));
    t14 = classes(stack({
      rowGap: "3 | h1"
    }));
    t15 = classes(stack({
      rowGap: "1 | h3"
    }));
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
  }
  let t16;
  if ($[32] !== address) {
    t16 = address && <p className={classes(textBlock({}))}>{address}</p>;
    $[32] = address;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== city || $[35] !== zipcode) {
    t17 = city && zipcode && <p className={classes(textBlock({}))}>{`${zipcode} ${city}`}</p>;
    $[34] = city;
    $[35] = zipcode;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <h4 className={classes(textBlock({
      fontWeight: "bold"
    }))}>{translateInline({
        en: "Opening hours",
        nl: "Openingstijden"
      })}</h4>;
    t20 = classes(locationOpeningTimesGrid, textStyle({
      capSize: "-1 | small"
    }));
    $[41] = t19;
    $[42] = t20;
  } else {
    t19 = $[41];
    t20 = $[42];
  }
  let t21;
  if ($[43] !== openingHours || $[44] !== title) {
    t21 = Object.entries(openingHours).map(t22 => {
      const [key, hoursArray] = t22;
      return <Fragment key={`${title}-${key}`}><span className={classes(textBlock({}))}>{dayLabels[key as keyof typeof dayLabels]}</span><div className={classes(stack({
          rowGap: "0 | p"
        }))}>{hoursArray.length === 0 && <span className={classes(textBlock({
            color: "positiveMuted"
          }))}>{translateInline({
              en: "Closed",
              nl: "Gesloten"
            })}</span>}{hoursArray.length > 0 && hoursArray.map((hours, i) => <span key={`${key}-${i}`} className={classes(textBlock({}))}>{hours}</span>)}</div></Fragment>;
    });
    $[43] = openingHours;
    $[44] = title;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = <div className={t20}>{t21}</div>;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== openingHoursMessageNL) {
    t23 = languageCode === "nl" && openingHoursMessageNL && <div className={classes(shelf({
      columnGap: "-1 | small"
    }))}><i className={classes(icon({
        icon: warningIcon,
        fill: "positiveMuted"
      }))} /><p className={classes(textBlock({
        color: "positiveMuted",
        capSize: "-1 | small"
      }))}>{openingHoursMessageNL}</p></div>;
    $[49] = openingHoursMessageNL;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t14 || $[52] !== t18 || $[53] !== t19 || $[54] !== t22 || $[55] !== t23) {
    t24 = <div className={t14}>{t18}{t19}{t22}{t23}</div>;
    $[51] = t14;
    $[52] = t18;
    $[53] = t19;
    $[54] = t22;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t13 || $[58] !== t24) {
    t25 = <div className={t13}>{t24}</div>;
    $[57] = t13;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== id || $[61] !== isOpened || $[62] !== t25) {
    t26 = <Collapse id={id} isOpened={isOpened}>{t25}</Collapse>;
    $[60] = id;
    $[61] = isOpened;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t12 || $[65] !== t26) {
    t27 = <div>{" "}{t12}{t26}</div>;
    $[64] = t12;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  return t27;
};